import { useState } from 'react';
import { Container, Paper, Typography, Box, Button } from '@mui/material';
import { useForm, SubmitHandler } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch } from '@app/store/configureStore';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import AppTextInput from '@app/components/AppTextInput';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from './accountSlice';
import { forgotPasswordValidationSchema } from './forgotPasswordValidation';

interface ForgotPasswordFormValues {
  email: string;
}

const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { control, handleSubmit } = useForm<ForgotPasswordFormValues>({
    mode: 'onTouched',
    resolver: yupResolver(forgotPasswordValidationSchema(t)),
  });

  const dispatch = useAppDispatch();

  const handleSubmitData: SubmitHandler<ForgotPasswordFormValues> = async ({
    email,
  }) => {
    setIsLoading(true);
    try {
      const isPasswordResetSuccessful = await dispatch(
        forgotPassword({ email })
      ).unwrap();
      if (isPasswordResetSuccessful) {
        navigate('/account/login');
      }
    } catch (error: unknown) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/account/login');
  };

  return (
    <Container
      component={Paper}
      maxWidth="sm"
      sx={{
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: 4,
      }}
    >
      <img
        src="/logo.svg"
        alt="Smartristo Logo"
        style={{
          width: '100px',
          marginBottom: '20px',
        }}
      />
      <Typography component="h1" variant="h5">
        {t('account.forgot-password.title')}
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(handleSubmitData)}
        noValidate
        sx={{ mt: 1 }}
      >
        <AppTextInput<ForgotPasswordFormValues>
          control={control}
          name="email"
          label={t('account.forgot-password.email')}
        />
        <LoadingButton
          loading={isLoading}
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          {t('common.submit')}
        </LoadingButton>
        <Button fullWidth variant="outlined" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
      </Box>
    </Container>
  );
};

export default ForgotPasswordForm;
